/**
 * FetchNoticesConfirmModal
 * ------------------------
 * Pre-fetch popup for shared clients with a 48-hour cooldown.
 *
 * Props:
 *   open            (bool)      Modal visibility
 *   onCancel        (fn)        Called when user dismisses
 *   onConfirm       (fn)        Called with the chosen client IDs. The
 *                               caller should send these as
 *                               `force_sync_client_ids` (and use them
 *                               as the regular `client_names` when
 *                               needed) so the BE 48-hour cooldown is
 *                               bypassed only for the selected rows.
 *   portal          (string)    "income_tax" | "gst" | "tds"
 *   clientIds       (string[])  Optional. When provided, the popup only
 *                               shows these client rows (e.g. when the
 *                               user pre-selected a subset on the page).
 *                               Otherwise the popup loads every client
 *                               the caller is allowed to fetch.
 *   confirmLabel    (string)    Custom CTA label
 *
 * Behaviour:
 *   • On open, GET /api/v1/clients/sync-status?portal=<portal>
 *   • Renders a table with: client name, last sync (hours/day ago),
 *     last sync user, cooldown badge
 *   • Bulk-select via checkbox column. Force-sync CTA stays disabled
 *     until at least one row is selected.
 *   • Validation message at the top summarising what will happen.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Modal, Table, Tag, Typography, Alert, Button, Empty } from "antd";
import { SyncOutlined, ClockCircleOutlined, UserOutlined } from "@ant-design/icons";
import { getData } from "../../api/apiService";

const { Text } = Typography;

const PORTAL_LABEL = {
  income_tax: "Income Tax",
  gst: "GST",
  tds: "TDS",
};

const portalKey = (serviceType) => {
  if (serviceType === "income-tax") return "income_tax";
  if (serviceType === "gstin") return "gst";
  return serviceType;
};

const formatRelative = (hoursSince) => {
  if (hoursSince === null || hoursSince === undefined) return "Never synced";
  if (hoursSince < 1) {
    const minutes = Math.max(1, Math.round(hoursSince * 60));
    return `${minutes} min ago`;
  }
  if (hoursSince < 24) {
    return `${hoursSince.toFixed(1)} hours ago`;
  }
  const days = Math.floor(hoursSince / 24);
  return days === 1 ? "1 day ago" : `${days} days ago`;
};

const FetchNoticesConfirmModal = ({
  open,
  onCancel,
  onConfirm,
  portal,
  clientIds,
  confirmLabel,
}) => {
  const portalParam = portalKey(portal);
  const portalLabel = PORTAL_LABEL[portalParam] || portalParam;

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [cooldownHours, setCooldownHours] = useState(48);
  const [selectedIds, setSelectedIds] = useState([]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const resp = await getData(
          `/api/v1/clients/sync-status?portal=${portalParam}`
        );
        const payload = resp?.data || resp?.message || resp || {};
        let data = payload?.data || [];
        if (Array.isArray(clientIds) && clientIds.length > 0) {
          const set = new Set(clientIds);
          data = data.filter((r) => set.has(r.client_id));
        }
        if (!cancelled) {
          setRows(data);
          setCooldownHours(payload?.cooldown_hours || 48);
          // Default selection: rows that are NOT in cooldown — those
          // can be fetched without a force. Force-sync CTA still
          // works on any selection; the popup gates only the
          // cooldown-active rows.
          setSelectedIds(data.map((r) => r.client_id));
        }
      } catch (err) {
        console.error("Failed to load sync status:", err);
        if (!cancelled) {
          setRows([]);
          setSelectedIds([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [open, portalParam, clientIds]);

  const cooldownCount = useMemo(
    () => rows.filter((r) => r.cooldown_active).length,
    [rows]
  );

  // Whether the *currently selected* rows include any cooldown-active
  // client. Drives the CTA wording: with a cooldown row in the selection
  // we surface the "Force sync" verb (server bypasses the 48-hour gate);
  // without one, "Sync" is enough — there's nothing to force past.
  const selectedHasCooldown = useMemo(() => {
    const selected = new Set(selectedIds);
    return rows.some((r) => selected.has(r.client_id) && r.cooldown_active);
  }, [rows, selectedIds]);
  const effectiveLabel =
    confirmLabel ||
    (selectedHasCooldown ? "Force sync notices" : "Sync notices");

  const columns = [
    {
      title: "Client",
      dataIndex: "client_name",
      key: "client_name",
      render: (v, row) => v || row.client_id,
    },
    {
      title: "Last sync",
      dataIndex: "hours_since",
      key: "hours_since",
      render: (v, row) => (
        <span>
          <ClockCircleOutlined style={{ marginRight: 4, color: "#888" }} />
          {formatRelative(v)}
        </span>
      ),
    },
    {
      title: "Last sync by",
      dataIndex: "last_sync_user_name",
      key: "last_sync_user_name",
      render: (v) =>
        v ? (
          <span>
            <UserOutlined style={{ marginRight: 4, color: "#888" }} />
            {v}
          </span>
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
    {
      title: "Cooldown",
      dataIndex: "cooldown_active",
      key: "cooldown_active",
      render: (active) =>
        active ? (
          <Tag color="orange">In cooldown</Tag>
        ) : (
          <Tag color="green">Eligible</Tag>
        ),
    },
  ];

  const handleConfirm = () => {
    onConfirm?.(selectedIds);
  };

  return (
    <Modal
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <SyncOutlined style={{ color: "#1890ff" }} />
          <span>{`Fetch ${portalLabel} notices`}</span>
        </div>
      }
      open={open}
      onCancel={onCancel}
      width={780}
      centered
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          key="confirm"
          type="primary"
          disabled={selectedIds.length === 0 || loading}
          onClick={handleConfirm}
        >
          {`${effectiveLabel} (${selectedIds.length})`}
        </Button>,
      ]}
      centered
      destroyOnHidden
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message={
          <span>
            {`Clients fetched within the last ${cooldownHours} hours are flagged "In cooldown". `}
            {cooldownCount > 0
              ? `Select the clients you want to re-fetch - the cooldown will be bypassed for your selection only.`
              : `All clients are eligible to fetch.`}
          </span>
        }
      />

      {rows.length === 0 && !loading ? (
        <Empty description="No clients available for this portal" />
      ) : (
        <Table
          rowKey="client_id"
          loading={loading}
          dataSource={rows}
          columns={columns}
          pagination={false}
          size="small"
          scroll={{ y: 320 }}
          rowSelection={{
            selectedRowKeys: selectedIds,
            onChange: (keys) => setSelectedIds(keys),
          }}
        />
      )}
    </Modal>
  );
};

export default FetchNoticesConfirmModal;
